interface Point { x: number; y: number }
export type CameraGesture =
  | { type: 'pan'; dx: number; dy: number }
  | { type: 'pinch'; ratio: number; from: Point; to: Point };

/** Pointer bookkeeping only: gestures never enter the simulation worker. */
export class CameraGestures {
  private readonly pointers = new Map<number, Point>();
  private start: Point = { x: 0, y: 0 };
  private moved = false;
  private suppressClick = false;
  public get active(): boolean { return this.pointers.size > 0; }

  public down(id: number, x: number, y: number): void {
    if (!this.active) {
      this.start = { x, y };
      this.moved = false;
      this.suppressClick = false;
    }
    this.pointers.set(id, { x, y });
    if (this.pointers.size > 1) this.moved = this.suppressClick = true;
  }

  public move(id: number, x: number, y: number): CameraGesture | undefined {
    const previous = this.pointers.get(id);
    if (!previous) return;
    if (this.pointers.size >= 2) {
      const pair = Array.from(this.pointers.entries()).slice(0, 2);
      const a = pair[0]!, b = pair[1]!;
      this.pointers.set(id, { x, y });
      if (id !== a[0] && id !== b[0]) return;
      const nextA = this.pointers.get(a[0])!, nextB = this.pointers.get(b[0])!;
      const distance = Math.hypot(a[1].x - b[1].x, a[1].y - b[1].y);
      const nextDistance = Math.hypot(nextA.x - nextB.x, nextA.y - nextB.y);
      if (distance < 1 || nextDistance < 1) return;
      return { type: 'pinch', ratio: nextDistance / distance,
        from: { x: (a[1].x + b[1].x) / 2, y: (a[1].y + b[1].y) / 2 },
        to: { x: (nextA.x + nextB.x) / 2, y: (nextA.y + nextB.y) / 2 } };
    }
    if (!this.moved && Math.hypot(x - this.start.x, y - this.start.y) < 5) return;
    this.moved = this.suppressClick = true;
    this.pointers.set(id, { x, y });
    return { type: 'pan', dx: x - previous.x, dy: y - previous.y };
  }

  public up(id: number): void { this.pointers.delete(id); }
  public consumeClick(): boolean {
    const suppress = this.suppressClick || this.pointers.size > 1;
    this.suppressClick = false;
    return suppress;
  }
}
